import { useNavigate } from "react-router-dom";
import type { GroupCycleRow, GroupView } from "@/services/types";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { Badge } from "@/components/base/Badge";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { cn } from "@/utils/cn";

const CYCLE_STATUS_BADGE: Record<GroupCycleRow["status"], { color: "success" | "warning" | "danger" | "secondary"; label: string }> = {
  completed: { color: "success", label: "Completed" },
  pending: { color: "warning", label: "Pending" },
  cancelled: { color: "danger", label: "Cancelled" },
};

const COLLECTION_BADGE_MAP: Record<string, { color: "success" | "warning" | "danger" | "secondary"; label: string }> = {
  paid: { color: "success", label: "Collected" },
  partial: { color: "warning", label: "Partial" },
  overdue: { color: "danger", label: "Overdue" },
  pending: { color: "secondary", label: "Pending" },
};

function renderPayoutBadge(status?: string | null) {
  switch (status) {
    case "disbursed":
    case "paid":
      return <Badge color="success" dot>Disbursed</Badge>;
    case "partially_disbursed":
    case "partially_paid":
      return <Badge color="warning" dot>Partially paid</Badge>;
    case "approved":
      return <Badge color="info" dot>Approved</Badge>;
    case "pending_approval":
      return <Badge color="warning" dot>Awaiting approval</Badge>;
    case "reversed":
      return <Badge color="danger" dot>Reversed</Badge>;
    case "pending":
    default:
      return <Badge color="secondary" dot>Pending</Badge>;
  }
}

export function CyclesTab({ group }: { group: GroupView }) {
  const navigate = useNavigate();
  const nameMap = new Map<string, string>();
  group.tickets.forEach((t) => {
    if (t.member) nameMap.set(t.member.id, t.member.fullName);
  });

  const columns: DataTableColumn<GroupCycleRow>[] = [
    {
      id: "cycle",
      header: "Cycle",
      accessor: (r) => (
        <span className="tnum font-semibold text-primary-700 hover:text-primary-900 hover:underline cursor-pointer">
          {r.cycle}
        </span>
      ),
      sortValue: (r) => r.cycle,
      sortable: true,
    },
    {
      id: "period",
      header: "Period",
      accessor: (r) => <span className="whitespace-nowrap text-foreground-700">{r.periodLabel}</span>,
      sortValue: (r) => r.periodLabel,
      sortable: true,
    },
    {
      id: "type",
      header: "Type",
      accessor: (r) =>
        r.isOrganizerReserved ? (
          <Badge color="accent" dot>Reserved organizer</Badge>
        ) : (
          <Badge variant="active" dot>Normal auction</Badge>
        ),
      sortValue: (r) => (r.isOrganizerReserved ? 1 : 0),
      sortable: true,
    },
    {
      id: "winner",
      header: "Winner",
      accessor: (r) =>
        r.winnerMemberId ? (
          <span className="font-medium text-foreground-900">{nameMap.get(r.winnerMemberId) ?? "—"}</span>
        ) : (
          <span className="text-foreground-400">Not decided</span>
        ),
      sortValue: (r) => (r.winnerMemberId ? nameMap.get(r.winnerMemberId) ?? "" : ""),
      sortable: true,
    },
    {
      id: "winningBid",
      header: "Winning Bid",
      align: "right",
      accessor: (r) => <CurrencyDisplay amount={r.winningBid} hideSymbol className={r.winningBid === 0 ? "text-foreground-400" : ""} />,
      sortValue: (r) => r.winningBid,
      sortable: true,
      footerValue: (rows) => (
        <CurrencyDisplay amount={rows.reduce((s, r) => s + r.winningBid, 0)} hideSymbol />
      ),
    },
    {
      id: "discount",
      header: "Discount",
      align: "right",
      accessor: (r) => <CurrencyDisplay amount={r.discount} hideSymbol className={r.discount === 0 ? "text-foreground-400" : ""} />,
      sortValue: (r) => r.discount,
      sortable: true,
      footerValue: (rows) => <CurrencyDisplay amount={rows.reduce((s, r) => s + r.discount, 0)} hideSymbol />,
    },
    {
      id: "dividend",
      header: "Dividend / member",
      align: "right",
      accessor: (r) => <CurrencyDisplay amount={r.perMemberDividend} hideSymbol className={r.perMemberDividend === 0 ? "text-foreground-400" : ""} />,
      sortValue: (r) => r.perMemberDividend,
      sortable: true,
    },
    {
      id: "commission",
      header: "Commission / member",
      align: "right",
      accessor: (r) => (
        <CurrencyDisplay
          amount={r.commissionPerMember ?? 0}
          hideSymbol
          className={(r.commissionPerMember ?? 0) === 0 ? "text-foreground-400" : ""}
        />
      ),
      sortValue: (r) => r.commissionPerMember ?? 0,
      sortable: true,
    },
    {
      id: "netInstallment",
      header: "Net installment",
      align: "right",
      accessor: (r) => <CurrencyDisplay amount={r.netInstallment} hideSymbol />,
      sortValue: (r) => r.netInstallment,
      sortable: true,
    },
    {
      id: "cycleStatus",
      header: "Auction status",
      accessor: (r) => {
        const meta = CYCLE_STATUS_BADGE[r.status] ?? { color: "secondary", label: "Pending" };
        return <Badge color={meta.color} dot>{meta.label}</Badge>;
      },
      sortValue: (r) => r.status,
      sortable: true,
    },
    {
      id: "collectionStatus",
      header: "Collection status",
      accessor: (r) => {
        const meta = COLLECTION_BADGE_MAP[r.collectionStatus ?? "pending"] ?? COLLECTION_BADGE_MAP.pending;
        return <Badge color={meta.color} dot>{meta.label}</Badge>;
      },
      sortValue: (r) => r.collectionStatus ?? "pending",
      sortable: true,
    },
    {
      id: "collectedPct",
      header: "Collection %",
      align: "right",
      accessor: (r) => {
        const pct = Math.round(r.collectedPct ?? 0);
        return (
          <div className="inline-flex items-center justify-end gap-2">
            <div className="h-1.5 w-10 rounded-full bg-background-200 overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  pct === 100 ? "bg-success-500" : pct > 0 ? "bg-primary-500" : "bg-transparent"
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className={cn("tnum text-xs font-medium", pct === 100 ? "text-success-700" : pct > 0 ? "text-foreground-700" : "text-foreground-400")}>
              {pct}%
            </span>
          </div>
        );
      },
      sortValue: (r) => r.collectedPct ?? 0,
      sortable: true,
    },
    {
      id: "payoutStatus",
      header: "Payout status",
      accessor: (r) => renderPayoutBadge(r.payoutStatus),
      sortValue: (r) => r.payoutStatus ?? "pending",
      sortable: true,
    },
  ];

  return (
    <div>
      <p className="mb-3 text-xs text-foreground-500">
        Click on any cycle row to open its auction console and details.
      </p>
      <DataTable<GroupCycleRow>
        columns={columns}
        rows={group.cycles}
        rowKey={(r) => String(r.cycle)}
        onRowClick={(r) => navigate(`/auction/${group.id}/${r.cycle}`)}
        emptyMessage="No cycles generated yet"
        emptyDescription="Activate the group to generate all monthly cycles."
        showTotals
        totalLabel={`${group.memberCount} cycles`}
        initialPageSize={10}
      />
    </div>
  );
}